import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { cn } from "../lib/utils"
import { appToast } from "../lib/app-dialogs"
import { BidComparisonPanel } from "./bid-comparison-panel"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

function currentOrganizationIdFromMeta() {
  const raw =
    typeof document !== "undefined"
      ? document.querySelector("meta[name='current-organization-id']")?.getAttribute("content")
      : null
  if (raw == null || String(raw).trim() === "") return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

function organizationLabel(org) {
  if (!org) return ""
  return org.company_code ? `${org.name} (${org.company_code})` : org.name
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {})
    },
    ...options
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.message || data?.error || `Request failed (${res.status})`
    const details = data?.details ? JSON.stringify(data.details) : ""
    throw new Error(details ? `${message}: ${details}` : message)
  }
  return data
}

async function fetchUpload(url, formData) {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken()
    },
    body: formData
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.message || data?.error || `Request failed (${res.status})`
    throw new Error(message)
  }
  return data
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function emptyLine() {
  return {
    item_name: "",
    impa_code: "",
    item_number: "",
    part_name: "",
    part_number: "",
    maker: "",
    service_kind: "",
    service_details: "",
    required_on: "",
    port_name: "",
    description: "",
    brand: "",
    product_name: "",
    color_grade: "",
    category: "",
    unit: "",
    quantity: "1",
    remarks: ""
  }
}

function formatQty(value) {
  if (value == null || value === "") return "1"
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return String(n)
}

function mapLine(line) {
  return {
    ...emptyLine(),
    item_name: line.item_name || line.product_name || line.description || "",
    impa_code: line.impa_code || "",
    item_number: line.item_number || "",
    part_name: line.part_name || "",
    part_number: line.part_number || "",
    maker: line.maker || "",
    service_kind: line.service_kind || "",
    service_details: line.service_details || "",
    required_on: line.required_on || "",
    port_name: line.port_name || "",
    description: line.description || "",
    brand: line.brand || "",
    product_name: line.product_name || "",
    color_grade: line.color_grade || "",
    category: line.category || "",
    unit: line.unit || "",
    quantity: formatQty(line.quantity),
    remarks: line.remarks || ""
  }
}

function TableInput({ value, onChange, disabled, placeholder, type = "text", required = false }) {
  return (
    <input
      type={type}
      required={required}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full min-w-0 rounded-md border border-input bg-card px-2 py-1.5 text-sm"
    />
  )
}

const LINE_COLUMNS = {
  store: [
    { key: "item_name", label: "Item name", required: true, className: "min-w-[220px]" },
    { key: "impa_code", label: "IMPA code", className: "min-w-[130px]" },
    { key: "quantity", label: "Required qty", required: true, className: "w-[110px]" },
    { key: "unit", label: "Unit", placeholder: "PCS, LTR…", className: "w-[90px]" }
  ],
  spare: [
    { key: "item_name", label: "Item name", required: true, className: "min-w-[150px]" },
    { key: "item_number", label: "Item number", className: "min-w-[120px]" },
    { key: "part_name", label: "Part name", required: true, className: "min-w-[150px]" },
    { key: "part_number", label: "Part number", required: true, className: "min-w-[120px]" },
    { key: "maker", label: "Maker", className: "min-w-[110px]" },
    { key: "quantity", label: "Qty", required: true, className: "w-[90px]" },
    { key: "unit", label: "Unit", className: "w-[80px]" }
  ],
  service: [
    { key: "service_kind", label: "Service required", required: true, placeholder: "e.g. Underwater inspection", className: "min-w-[180px]" },
    { key: "port_name", label: "Port", required: true, placeholder: "e.g. Singapore", className: "min-w-[140px]" },
    { key: "required_on", label: "Required on", type: "date", className: "w-[150px]" },
    { key: "service_details", label: "Service details", placeholder: "Scope and instructions", className: "min-w-[240px]" }
  ]
}

const IMPORT_PREVIEW_COLUMNS = {
  store: [
    { key: "item_name", label: "Item name" },
    { key: "impa_code", label: "IMPA code" },
    { key: "quantity", label: "Qty" },
    { key: "unit", label: "Unit" }
  ],
  spare: [
    { key: "item_name", label: "Item name" },
    { key: "item_number", label: "Item number" },
    { key: "part_name", label: "Part name" },
    { key: "part_number", label: "Part number" },
    { key: "maker", label: "Maker" },
    { key: "quantity", label: "Qty" },
    { key: "unit", label: "Unit" }
  ]
}

function isBlankLine(line, kind) {
  if (kind === "store") return !String(line.item_name || "").trim()
  if (kind === "spare") {
    return !String(line.item_name || "").trim() && !String(line.part_name || "").trim() && !String(line.part_number || "").trim()
  }
  return !String(line.service_kind || "").trim() && !String(line.service_details || "").trim()
}

const headerControlClass = cn(
  "box-border h-9 min-h-9 max-h-9 w-full min-w-0 rounded-lg border border-input bg-card px-2.5 py-0 text-sm leading-9",
  "[&[type=date]]:h-9 [&[type=date]]:min-h-9 [&[type=date]]:max-h-9 [&[type=date]]:py-0 [&[type=date]]:leading-9",
  "[&::-webkit-datetime-edit]:p-0 [&::-webkit-datetime-edit]:leading-9",
  "[&::-webkit-calendar-picker-indicator]:my-0 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4"
)

function HeaderField({ label, className, title, children, as: Tag = "label" }) {
  return (
    <Tag className={cn("grid min-w-0 grid-rows-[1.25rem_2.25rem] content-start gap-1", className)} title={title}>
      <span className="truncate whitespace-nowrap text-xs leading-5 text-muted-foreground">{label}</span>
      {children}
    </Tag>
  )
}

function vendorColumnLabel(item) {
  const names = (item.vendor_names || []).filter(Boolean)
  if (names.length) return names.join(", ")
  const invited = (item.vendor_rfqs || []).map((rfq) => rfq.vendor?.name).filter(Boolean)
  if (invited.length) return invited.join(", ")
  return "—"
}

function monitorStatus(item) {
  const aliases = {
    ready: "draft",
    quote_received: "full_quote_received",
    quote_approved: "full_quote_received",
    split: "full_quote_received"
  }
  return aliases[item.status] || item.status
}

function VendorHeaderSelect({ vendors, selectedIds, onChange, disabled, companyName }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const rootRef = useRef(null)

  useEffect(() => {
    function onDocClick(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return vendors
    return vendors.filter((v) => {
      const hay = [v.name, v.email, v.primary_contact_email].filter(Boolean).join(" ").toLowerCase()
      return hay.includes(q)
    })
  }, [vendors, query])

  const selectedNames = selectedIds
    .map((id) => vendors.find((row) => String(row.id) === String(id))?.name)
    .filter(Boolean)
  const buttonLabel =
    selectedNames.length === 0
      ? vendors.length === 0
        ? `No vendors for ${companyName}`
        : "Select vendors…"
      : selectedNames.length === 1
        ? selectedNames[0]
        : `${selectedNames.length} vendors`

  return (
    <div ref={rootRef} className={cn("relative min-w-0", open && "z-50")}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className={cn(headerControlClass, "flex items-center justify-between gap-1 text-left")}
        title={selectedNames.join(", ") || buttonLabel}
      >
        <span className="min-w-0 truncate">{buttonLabel}</span>
        <span className="shrink-0 text-muted-foreground">▾</span>
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-[70] mt-1 w-full min-w-full max-w-[min(28rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-input bg-card p-2 shadow-lg">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search vendors…"
            className="mb-2 box-border h-8 w-full min-w-0 rounded-md border border-input bg-card px-2 text-sm"
          />
          <div className="max-h-48 space-y-0.5 overflow-y-auto overflow-x-hidden">
            {filtered.length === 0 ? (
              <p className="px-1 py-2 text-xs text-muted-foreground">
                {vendors.length === 0
                  ? `Add suppliers under Purchases → Vendors for ${companyName}.`
                  : "No vendors match this search."}
              </p>
            ) : (
              filtered.map((v) => {
                const checked = selectedIds.includes(String(v.id))
                const email = v.email || v.primary_contact_email
                return (
                  <label
                    key={v.id}
                    className="flex w-full min-w-0 cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-muted/60"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      className="mt-0.5 h-4 w-4 shrink-0"
                      onChange={(e) => {
                        onChange(
                          e.target.checked
                            ? [...selectedIds, String(v.id)]
                            : selectedIds.filter((id) => id !== String(v.id))
                        )
                      }}
                    />
                    <span className="min-w-0 flex-1 leading-snug">
                      <span className="block break-words font-medium">{v.name}</span>
                      {email ? <span className="mt-0.5 block break-all text-xs text-muted-foreground">{email}</span> : null}
                    </span>
                  </label>
                )
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

const STATUS_LABELS = {
  draft: "Draft",
  sent_for_quote: "Send to Vendors",
  partial_quote_received: "Partial Quote Received",
  full_quote_received: "Full Quote Received",
  po_issued: "PO issued",
  requisition_delivered: "Requisition Delivered",
  invoice_received: "Invoice Received",
  invoice_paid: "Invoice Paid",
  cancelled: "Cancelled"
}

const FILTER_STATUSES = [
  "draft",
  "sent_for_quote",
  "partial_quote_received",
  "full_quote_received",
  "po_issued",
  "requisition_delivered",
  "invoice_received",
  "invoice_paid"
]

const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700",
  sent_for_quote: "bg-indigo-100 text-indigo-800",
  partial_quote_received: "bg-amber-100 text-amber-900",
  full_quote_received: "bg-emerald-100 text-emerald-900",
  po_issued: "bg-blue-100 text-blue-900",
  requisition_delivered: "bg-teal-100 text-teal-900",
  invoice_received: "bg-orange-100 text-orange-900",
  invoice_paid: "bg-emerald-200 text-emerald-950",
  cancelled: "bg-rose-100 text-rose-800"
}

const STATUS_PIPELINE = [
  "sent_for_quote",
  "partial_quote_received",
  "full_quote_received",
  "po_issued",
  "requisition_delivered",
  "invoice_received",
  "invoice_paid"
]

const KIND_LABELS = {
  store: "Store",
  spare: "Spare parts",
  service: "Services"
}

const KIND_HINTS = {
  store: "Item name, IMPA code, and required quantity.",
  spare: "Item name, item number, part name, and part number.",
  service: "Service required, details, date, and port."
}

const KIND_BADGE = {
  store: "bg-sky-100 text-sky-800",
  spare: "bg-amber-100 text-amber-900",
  service: "bg-violet-100 text-violet-900"
}

export function RequisitionsPanel({ canEnter = true, currentOrganization = null }) {
  const [items, setItems] = useState([])
  const [vendors, setVendors] = useState([])
  const [company, setCompany] = useState(currentOrganization)
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [kindFilter, setKindFilter] = useState("all")
  const [editingId, setEditingId] = useState(null)
  const [compareId, setCompareId] = useState(null)
  const [selectedVendorIds, setSelectedVendorIds] = useState([])
  const [inviteVendorId, setInviteVendorId] = useState("")
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteName, setInviteName] = useState("")
  const [importPreview, setImportPreview] = useState(null)
  const [importing, setImporting] = useState(false)
  const [numberTick, setNumberTick] = useState(0)
  const fileInputRef = useRef(null)
  const [form, setForm] = useState({
    requisition_kind: "",
    heading: "",
    requisition_date: todayISO(),
    status: "draft",
    currency: "USD",
    notes: "",
    customer_po_number: "",
    customer_po_date: "",
    quote_deadline: "",
    requisition_number: "",
    lines: [emptyLine()]
  })

  async function loadAll() {
    setLoading(true)
    setError("")
    try {
      const [reqs, vends] = await Promise.all([
        fetchJson("/api/purchases/purchase_requisitions"),
        fetchJson("/api/purchases/vendors")
      ])
      setItems(Array.isArray(reqs) ? reqs : [])
      setVendors(Array.isArray(vends) ? vends : [])
    } catch (e) {
      setError(e.message || "Failed to load requisitions")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const data = await fetchJson("/api/organizations")
        if (!alive) return
        const list = Array.isArray(data) ? data : []
        const metaId = currentOrganizationIdFromMeta()
        const match =
          list.find((o) => Number(o.id) === Number(metaId)) ||
          list.find((o) => Number(o.id) === Number(currentOrganization?.id)) ||
          currentOrganization ||
          list[0] ||
          null
        if (match) setCompany(match)
      } catch {
        if (currentOrganization) setCompany(currentOrganization)
      }
    })()
    return () => {
      alive = false
    }
  }, [currentOrganization?.id])

  useEffect(() => {
    loadAll()
  }, [company?.id])

  useEffect(() => {
    if (editingId) return
    const sp = new URLSearchParams({
      requisition_date: form.requisition_date || todayISO()
    })
    fetchJson(`/api/purchases/purchase_requisitions/next_number?${sp}`)
      .then((data) => setForm((f) => ({ ...f, requisition_number: data.requisition_number || "" })))
      .catch(() => {})
  }, [form.requisition_date, editingId, company?.id, numberTick])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((item) => {
      if (statusFilter !== "all" && monitorStatus(item) !== statusFilter) return false
      if (kindFilter !== "all" && item.requisition_kind !== kindFilter) return false
      if (!q) return true
      const hay = [
        item.requisition_number,
        item.heading,
        ...(item.vendor_names || []),
        ...((item.vendor_rfqs || []).map((rfq) => rfq.vendor?.name))
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return hay.includes(q)
    })
  }, [items, query, statusFilter, kindFilter])

  const companyName = company?.name || "this company"
  const companyLabel = organizationLabel(company) || "Loading company…"

  function resetForm() {
    setEditingId(null)
    setNumberTick((n) => n + 1)
    setSelectedVendorIds([])
    setInviteVendorId("")
    setInviteEmail("")
    setInviteName("")
    setImportPreview(null)
    setForm({
      requisition_kind: "",
      heading: "",
      requisition_date: todayISO(),
      status: "draft",
      currency: "USD",
      notes: "",
      customer_po_number: "",
      customer_po_date: "",
      quote_deadline: "",
      requisition_number: "",
      lines: [emptyLine()]
    })
  }

  function editItem(item) {
    setEditingId(item.id)
    setSelectedVendorIds((item.vendor_rfqs || []).map((r) => String(r.vendor_id)))
    setInviteVendorId(String((item.vendor_rfqs || [])[0]?.vendor_id || ""))
    setForm({
      requisition_kind: item.requisition_kind || "store",
      heading: item.heading || "",
      requisition_date: item.requisition_date || todayISO(),
      status: item.status || "draft",
      currency: item.currency || "USD",
      notes: item.notes || "",
      customer_po_number: item.customer_po_number || "",
      customer_po_date: item.customer_po_date || "",
      quote_deadline: item.quote_deadline || "",
      requisition_number: item.requisition_number || "",
      lines:
        (item.purchase_requisition_lines || []).length > 0
          ? item.purchase_requisition_lines.map(mapLine)
          : [emptyLine()]
    })
    document.getElementById("requisition-form")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  async function persistRequisition({ send = false } = {}) {
    if (!canEnter) return false
    if (send && selectedVendorIds.length === 0) {
      setError("Select at least one vendor to send the RFQ.")
      return false
    }
    setSaving(true)
    setError("")
    try {
      const payload = {
        purchase_requisition: {
          requisition_kind: form.requisition_kind,
          heading: form.heading,
          requisition_date: form.requisition_date,
          currency: form.currency,
          notes: form.notes,
          customer_po_number: form.customer_po_number || null,
          customer_po_date: form.customer_po_date || null,
          quote_deadline: form.quote_deadline || null,
          lines: form.lines.map((line) => ({
            item_name: line.item_name,
            impa_code: line.impa_code,
            item_number: line.item_number,
            part_name: line.part_name,
            part_number: line.part_number,
            maker: line.maker,
            service_kind: line.service_kind,
            service_details: line.service_details,
            required_on: line.required_on || null,
            port_name: line.port_name,
            description: line.description,
            brand: line.brand,
            product_name: line.product_name || line.item_name,
            color_grade: line.color_grade,
            category: line.category,
            unit: line.unit,
            quantity: line.quantity || "1",
            remarks: line.remarks
          }))
        },
        vendor_ids: selectedVendorIds.map(Number),
        send
      }
      if (editingId) {
        await fetchJson(`/api/purchases/purchase_requisitions/${editingId}`, {
          method: "PATCH",
          body: JSON.stringify({ purchase_requisition: payload.purchase_requisition })
        })
        if (send) {
          const sent = await fetchJson(`/api/purchases/purchase_requisitions/${editingId}/send_to_vendors`, {
            method: "POST",
            body: JSON.stringify({ vendor_ids: selectedVendorIds.map(Number) })
          })
          appToast(`RFQ sent to ${selectedVendorIds.length} vendor(s).`)
          await loadAll()
          editItem(sent)
          return true
        }
      } else {
        const created = await fetchJson("/api/purchases/purchase_requisitions", {
          method: "POST",
          body: JSON.stringify(payload)
        })
        if (send) {
          appToast(`RFQ sent to ${selectedVendorIds.length} vendor(s).`)
        } else {
          appToast(`Requisition ${created.requisition_number || ""} saved as draft. You can send it for quote later.`)
        }
      }
      resetForm()
      await loadAll()
      return true
    } catch (err) {
      setError(err.message || (send ? "Failed to send RFQ" : "Save failed"))
      return false
    } finally {
      setSaving(false)
    }
  }

  async function onSubmit(e) {
    e.preventDefault()
    await persistRequisition({ send: false })
  }

  async function saveAndSend() {
    const formEl = document.querySelector("#requisition-form form")
    if (formEl && !formEl.reportValidity()) return
    await persistRequisition({ send: true })
  }

  async function sendToVendors() {
    if (!editingId || selectedVendorIds.length === 0) {
      setError("Select at least one vendor to send the RFQ.")
      return
    }
    setSaving(true)
    setError("")
    try {
      await fetchJson(`/api/purchases/purchase_requisitions/${editingId}/send_to_vendors`, {
        method: "POST",
        body: JSON.stringify({ vendor_ids: selectedVendorIds.map(Number) })
      })
      appToast(`RFQ sent to ${selectedVendorIds.length} vendor(s).`)
      await loadAll()
      const refreshed = await fetchJson(`/api/purchases/purchase_requisitions/${editingId}`)
      editItem(refreshed)
    } catch (err) {
      setError(err.message || "Failed to send RFQ")
    } finally {
      setSaving(false)
    }
  }

  async function remindVendors() {
    if (!editingId) return
    setSaving(true)
    setError("")
    try {
      const result = await fetchJson(`/api/purchases/purchase_requisitions/${editingId}/remind_vendors`, {
        method: "POST",
        body: JSON.stringify({})
      })
      appToast(`Reminder sent to ${result.reminded || 0} vendor(s).`)
      if (result.requisition) editItem(result.requisition)
      else await loadAll()
    } catch (err) {
      setError(err.message || "Failed to send reminders")
    } finally {
      setSaving(false)
    }
  }

  function updateLine(index, key, value) {
    setForm((f) => {
      const lines = f.lines.slice()
      lines[index] = { ...lines[index], [key]: value }
      return { ...f, lines }
    })
  }

  function changeKind(kind) {
    setImportPreview(null)
    setForm((f) => ({
      ...f,
      requisition_kind: kind,
      lines: editingId ? f.lines : [emptyLine()]
    }))
  }

  async function onPickSpreadsheet(e) {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    if (form.requisition_kind !== "store" && form.requisition_kind !== "spare") {
      setError("Excel import is available for store and spare parts requisitions.")
      return
    }
    setImporting(true)
    setError("")
    try {
      const body = new FormData()
      body.append("file", file)
      body.append("kind", form.requisition_kind)
      const data = await fetchUpload("/api/purchases/purchase_requisitions/preview_import", body)
      setImportPreview(data)
    } catch (err) {
      setImportPreview(null)
      setError(err.message || "Could not read the spreadsheet")
    } finally {
      setImporting(false)
    }
  }

  function applyImport(mode) {
    if (!importPreview?.rows?.length) return
    const incoming = importPreview.rows.map((row) => ({
      ...emptyLine(),
      ...row,
      quantity: formatQty(row.quantity)
    }))
    setForm((f) => {
      const existing = (f.lines || []).filter((line) => !isBlankLine(line, f.requisition_kind))
      const merged = mode === "append" ? [...existing, ...incoming] : incoming
      return { ...f, lines: merged.length ? merged : [emptyLine()] }
    })
    appToast(`Added ${incoming.length} line item(s) from ${importPreview.filename}.`)
    setImportPreview(null)
  }

  function renderLineTable() {
    const columns = LINE_COLUMNS[form.requisition_kind] || LINE_COLUMNS.store
    const disabled = !canEnter
    return (
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-max border-collapse text-left text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
              {columns.map((col) => (
                <th key={col.key} className={cn("px-2 py-2 font-medium", col.className)}>
                  {col.label}
                  {col.required ? " *" : ""}
                </th>
              ))}
              <th className="w-[72px] px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {form.lines.map((line, index) => (
              <tr key={index} className="border-b border-border/70 align-middle">
                {columns.map((col) => (
                  <td key={col.key} className={cn("px-2 py-1.5", col.className)}>
                    <TableInput
                      type={col.type || "text"}
                      required={col.required}
                      value={line[col.key] || ""}
                      disabled={disabled}
                      placeholder={col.placeholder}
                      onChange={(v) => updateLine(index, col.key, v)}
                    />
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right">
                  {form.lines.length > 1 && canEnter ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-8 px-2 text-xs"
                      onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== index) }))}
                    >
                      Remove
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="grid gap-6">
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      {compareId ? (
        <BidComparisonPanel
          requisitionId={compareId}
          canEnter={canEnter}
          onClose={() => {
            setCompareId(null)
            loadAll()
          }}
        />
      ) : (
        <>
      <Card id="requisition-form">
        <CardHeader>
          <CardTitle>{editingId ? "Edit Requisition" : "Create Requisition"}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="grid gap-3">
            <label className="grid gap-0.5 text-xs">
              <span className="text-muted-foreground">This company</span>
              <input value={companyLabel} readOnly className={cn(headerControlClass, "bg-muted")} />
              <span className="text-[11px] leading-tight text-muted-foreground">
                This requisition belongs only to {companyName}. Switch company in the top bar to work in another
                company.
              </span>
            </label>
            <div className="grid grid-cols-6 items-end gap-x-3 gap-y-3 overflow-visible">
              <HeaderField
                label="Requisition type"
                title={form.requisition_kind ? KIND_HINTS[form.requisition_kind] : "Store, spare parts, or services."}
              >
                <select
                  required
                  value={form.requisition_kind}
                  disabled={!canEnter || Boolean(editingId)}
                  onChange={(e) => changeKind(e.target.value)}
                  className={headerControlClass}
                  title={form.requisition_kind ? KIND_HINTS[form.requisition_kind] : "Store, spare parts, or services."}
                >
                  <option value="">Select type…</option>
                  {Object.entries(KIND_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </HeaderField>
              <HeaderField
                as="div"
                label="Vendors"
                title={`Choose ${companyName} suppliers to receive the RFQ. Optional for a draft.`}
                className="relative z-20 col-span-2 overflow-visible"
              >
                <VendorHeaderSelect
                  vendors={vendors}
                  selectedIds={selectedVendorIds}
                  onChange={setSelectedVendorIds}
                  disabled={!canEnter}
                  companyName={companyName}
                />
              </HeaderField>
              <HeaderField label="Req. number">
                <input value={form.requisition_number} readOnly className={cn(headerControlClass, "bg-muted")} />
              </HeaderField>
              <HeaderField label="Date">
                <input
                  type="date"
                  required
                  value={form.requisition_date}
                  disabled={!canEnter}
                  onChange={(e) => setForm((f) => ({ ...f, requisition_date: e.target.value }))}
                  className={headerControlClass}
                />
              </HeaderField>
              <HeaderField label="Currency">
                <input
                  value={form.currency}
                  disabled={!canEnter}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  className={headerControlClass}
                />
              </HeaderField>
            </div>

            <div className="grid grid-cols-6 items-end gap-x-3 gap-y-3">
              <HeaderField label="Heading" className="col-span-2">
                <input
                  value={form.heading}
                  disabled={!canEnter}
                  onChange={(e) => setForm((f) => ({ ...f, heading: e.target.value }))}
                  className={headerControlClass}
                  placeholder={
                    form.requisition_kind === "spare"
                      ? "e.g. Main engine spare parts"
                      : form.requisition_kind === "service"
                        ? "e.g. Underwater hull inspection"
                        : "e.g. Deck stores"
                  }
                />
              </HeaderField>
              <HeaderField label="Customer PO #">
                <input
                  value={form.customer_po_number}
                  disabled={!canEnter}
                  onChange={(e) => setForm((f) => ({ ...f, customer_po_number: e.target.value }))}
                  className={headerControlClass}
                  placeholder="PO received from customer"
                />
              </HeaderField>
              <HeaderField label="Customer PO date">
                <input
                  type="date"
                  value={form.customer_po_date}
                  disabled={!canEnter}
                  onChange={(e) => setForm((f) => ({ ...f, customer_po_date: e.target.value }))}
                  className={headerControlClass}
                />
              </HeaderField>
              <HeaderField label="Quote deadline">
                <input
                  type="date"
                  value={form.quote_deadline}
                  disabled={!canEnter}
                  onChange={(e) => setForm((f) => ({ ...f, quote_deadline: e.target.value }))}
                  className={headerControlClass}
                />
              </HeaderField>
              <HeaderField label="Status">
                <div className={cn(headerControlClass, "flex items-center bg-muted")}>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs font-medium",
                      STATUS_BADGE[monitorStatus({ status: form.status })] || "bg-muted"
                    )}
                  >
                    {STATUS_LABELS[monitorStatus({ status: form.status })] || form.status || "Draft"}
                  </span>
                </div>
              </HeaderField>
            </div>

            <label className="grid gap-0.5 text-xs">
              <span className="text-muted-foreground">Notes</span>
              <textarea
                value={form.notes}
                disabled={!canEnter}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="min-h-[64px] w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm"
              />
            </label>

            {form.requisition_kind ? (
              <>
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">
                  {form.requisition_kind === "service" ? "Services" : "Line items"}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  {canEnter && (form.requisition_kind === "store" || form.requisition_kind === "spare") ? (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                        className="hidden"
                        onChange={onPickSpreadsheet}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="h-8"
                        onClick={() =>
                          window.open(
                            `/api/purchases/purchase_requisitions/import_template?kind=${form.requisition_kind}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        Download template
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-8"
                        disabled={importing}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {importing ? "Reading…" : "Upload Excel"}
                      </Button>
                    </>
                  ) : null}
                  {canEnter ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8"
                      onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }))}
                    >
                      + Add {form.requisition_kind === "service" ? "service" : "line"}
                    </Button>
                  ) : null}
                </div>
              </div>

              {importPreview ? (
                <div className="mb-3 grid gap-2 rounded-lg border border-border bg-muted/30 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">Preview: {importPreview.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {importPreview.rows?.length || 0} line(s)
                        {importPreview.skipped ? ` · ${importPreview.skipped} empty row(s) skipped` : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="ghost" className="h-8" onClick={() => setImportPreview(null)}>
                        Cancel
                      </Button>
                      <Button type="button" variant="outline" className="h-8" onClick={() => applyImport("append")}>
                        Add to existing
                      </Button>
                      <Button type="button" className="h-8" onClick={() => applyImport("replace")}>
                        Replace line items
                      </Button>
                    </div>
                  </div>
                  {(importPreview.warnings || []).length > 0 ? (
                    <ul className="list-disc pl-4 text-xs text-amber-800">
                      {importPreview.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-max text-left text-sm">
                      <thead>
                        <tr className="border-b text-xs text-muted-foreground">
                          {(IMPORT_PREVIEW_COLUMNS[form.requisition_kind] || []).map((col) => (
                            <th key={col.key} className="px-2 py-1.5 font-medium">
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(importPreview.rows || []).slice(0, 25).map((row, idx) => (
                          <tr key={idx} className="border-b border-border/70">
                            {(IMPORT_PREVIEW_COLUMNS[form.requisition_kind] || []).map((col) => (
                              <td key={col.key} className="px-2 py-1.5">
                                {row[col.key] || "—"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(importPreview.rows || []).length > 25 ? (
                      <p className="pt-1 text-xs text-muted-foreground">
                        Showing 25 of {importPreview.rows.length} rows.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {renderLineTable()}
            </div>

            {editingId ? (
            <div className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold">Vendor portal</h3>
                    <div className="flex flex-wrap gap-1">
                      {STATUS_PIPELINE.map((step) => (
                        <span
                          key={step}
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-medium",
                            monitorStatus({ status: form.status }) === step
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          )}
                        >
                          {STATUS_LABELS[step]}
                        </span>
                      ))}
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  RFQ recipients are selected in Vendors above.
                  {selectedVendorIds.length > 0 ? ` Selected ${selectedVendorIds.length} of ${vendors.length}.` : ""}
                  {form.quote_deadline ? ` Deadline ${form.quote_deadline}.` : ""}
                </p>
                <div className="grid gap-2 rounded-md bg-muted/40 p-2 md:grid-cols-4">
                  <select
                    value={inviteVendorId}
                    onChange={(e) => {
                      const id = e.target.value
                      setInviteVendorId(id)
                      const v = vendors.find((row) => String(row.id) === id)
                      if (v?.email) setInviteEmail(v.email)
                      if (v?.primary_contact_name) setInviteName(v.primary_contact_name)
                    }}
                    className="rounded border border-input px-2 py-1 text-sm"
                  >
                    <option value="">Select vendor for portal invite…</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                  <input
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="rounded border border-input px-2 py-1 text-sm"
                    placeholder="Vendor login email"
                  />
                  <input
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    className="rounded border border-input px-2 py-1 text-sm"
                    placeholder="Contact name"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!canEnter || saving}
                    onClick={async () => {
                      if (!inviteVendorId || !inviteEmail) {
                        setError("Select a vendor and enter an email to invite a portal user.")
                        return
                      }
                      setSaving(true)
                      setError("")
                      try {
                        const result = await fetchJson("/api/purchases/vendor_portal_users", {
                          method: "POST",
                          body: JSON.stringify({
                            vendor_id: Number(inviteVendorId),
                            email: inviteEmail,
                            name: inviteName
                          })
                        })
                        const pwd = result.temporary_password
                          ? ` Temporary password: ${result.temporary_password}`
                          : ""
                        appToast(`Vendor portal user ready for ${result.user.email}.${pwd}`)
                      } catch (err) {
                        setError(err.message || "Invite failed")
                      } finally {
                        setSaving(false)
                      }
                    }}
                  >
                    Invite portal login
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" disabled={!canEnter || saving} onClick={remindVendors}>
                    Remind pending vendors
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" variant="outline" disabled={!canEnter || saving || !form.requisition_kind}>
                {saving ? "Saving…" : editingId ? "Update" : "Save draft"}
              </Button>
              <Button
                type="button"
                disabled={!canEnter || saving || !form.requisition_kind}
                onClick={saveAndSend}
              >
                {saving ? "Sending…" : editingId ? "Send for quote" : "Save & send"}
              </Button>
              {editingId ? (
                <Button type="button" variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
              ) : null}
              {editingId ? (
                <Button type="button" variant="outline" onClick={() => setCompareId(editingId)}>
                  Bid comparison
                </Button>
              ) : null}
            </div>
              </>
            ) : null}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>Requisitions</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              className="rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm"
            >
              <option value="all">All types</option>
              {Object.entries(KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm"
            >
              <option value="all">All statuses</option>
              {FILTER_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {STATUS_LABELS[value]}
                </option>
              ))}
            </select>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search req #, heading, vendor…"
              className="w-full max-w-xs rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {items.length === 0
                ? `No requisitions yet. Create one above and send it to vendors to start monitoring quotes for ${companyName}.`
                : "No requisitions match this search or filter."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="px-2 py-2">Req. number</th>
                    <th className="px-2 py-2">Type</th>
                    <th className="px-2 py-2">Vendors</th>
                    <th className="px-2 py-2">Heading</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Quotes</th>
                    <th className="px-2 py-2">Deadline</th>
                    <th className="px-2 py-2">Date</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr key={item.id} className="border-b border-border/70">
                      <td className="px-2 py-2 font-medium text-primary">{item.requisition_number}</td>
                      <td className="px-2 py-2">
                        <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", KIND_BADGE[item.requisition_kind] || "bg-muted")}>
                          {item.kind_label || KIND_LABELS[item.requisition_kind] || item.requisition_kind || "Store"}
                        </span>
                      </td>
                      <td className="px-2 py-2">{vendorColumnLabel(item)}</td>
                      <td className="px-2 py-2">{item.heading || "—"}</td>
                      <td className="px-2 py-2">
                        <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", STATUS_BADGE[monitorStatus(item)] || "bg-muted")}>
                          {item.status_label || STATUS_LABELS[monitorStatus(item)] || item.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 tabular-nums text-xs text-muted-foreground">
                        {item.quotes_submitted ?? item.rfq_responded_count ?? 0}/
                        {item.quotes_invited ?? item.rfq_sent_count ?? (item.vendor_rfqs || []).length}
                      </td>
                      <td className="px-2 py-2">{item.quote_deadline || "—"}</td>
                      <td className="px-2 py-2">{item.requisition_date}</td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button type="button" variant="ghost" className="h-8 px-2" onClick={() => editItem(item)}>
                            Open
                          </Button>
                          {(monitorStatus(item) === "draft") && (item.quotes_invited ?? item.rfq_sent_count ?? 0) === 0 ? (
                            <Button type="button" variant="outline" className="h-8 px-2" onClick={() => editItem(item)}>
                              Send for quote
                            </Button>
                          ) : null}
                          <Button type="button" variant="outline" className="h-8 px-2" onClick={() => setCompareId(item.id)}>
                            Compare
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}
    </div>
  )
}
